import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";

const Navbar = () => {
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [servicesOpen, setServicesOpen] = useState(false);
  const [mobileServicesOpen, setMobileServicesOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    setMenuOpen(false);
    setServicesOpen(false);
    setMobileServicesOpen(false);
  }, [location.pathname]);

  const toggleLang = () => {
    const newLang = i18n.language === "en" ? "es" : "en";
    i18n.changeLanguage(newLang);
    localStorage.setItem("lang", newLang);
  };

  const isActive = (path: string) =>
    location.pathname === path || location.pathname.startsWith(path + "/");

  const isEn = i18n.language === "en";

  const megaMenu = [
    {
      category: isEn ? "Handyman Services" : "Servicios de Reparación",
      icon: "ri-tools-line",
      color: "text-brand-navy",
      bgColor: "bg-brand-navy/10",
      subcategories: [
        {
          label: isEn ? "Basic Home Repairs" : "Reparaciones Básicas",
          icon: "ri-home-gear-line",
          items: [
            { slug: "door-repairs", label: isEn ? "Door Repairs" : "Reparación de Puertas" },
            { slug: "cabinet-fixes", label: isEn ? "Cabinet & Drawer Fixes" : "Gabinetes y Cajones" },
            { slug: "baseboard-trim", label: isEn ? "Baseboards & Trim" : "Zócalos y Molduras" },
          ],
        },
        {
          label: isEn ? "Painting & Touch-Ups" : "Pintura y Retoques",
          icon: "ri-paint-brush-line",
          items: [
            { slug: "interior-painting", label: isEn ? "Interior Painting" : "Pintura Interior" },
            { slug: "patch-paint", label: isEn ? "Patch & Paint Walls" : "Parchar y Pintar" },
            { slug: "tenant-touchups", label: isEn ? "Touch-ups (Tenants)" : "Retoques entre Inquilinos" },
          ],
        },
        {
          label: isEn ? "Installation Services" : "Servicios de Instalación",
          icon: "ri-tv-line",
          items: [
            { slug: "tv-mounting", label: isEn ? "TV Mounting" : "Instalación de TV" },
            { slug: "curtain-rods-blinds", label: isEn ? "Curtain Rods & Blinds" : "Cortinas y Persianas" },
            { slug: "shelves-wall-decor", label: isEn ? "Shelves & Wall Decor" : "Estantes y Decoración" },
            { slug: "furniture-assembly", label: isEn ? "Furniture Assembly" : "Ensamblaje de Muebles" },
          ],
        },
        {
          label: isEn ? "Exterior & Maintenance" : "Exterior y Mantenimiento",
          icon: "ri-leaf-line",
          items: [
            { slug: "pressure-washing", label: isEn ? "Pressure Washing" : "Lavado a Presión" },
            { slug: "gutter-cleaning", label: isEn ? "Gutter Cleaning" : "Limpieza de Canaletas" },
            { slug: "fence-repairs", label: isEn ? "Fence Repairs" : "Reparación de Cercas" },
            { slug: "landscaping", label: isEn ? "Small Landscaping" : "Jardinería Pequeña" },
          ],
        },
      ],
    },
    {
      category: isEn ? "Cleaning Services" : "Servicios de Limpieza",
      icon: "ri-sparkling-2-line",
      color: "text-brand-green",
      bgColor: "bg-brand-green/10",
      subcategories: [
        {
          label: isEn ? "All Cleaning" : "Toda la Limpieza",
          icon: "ri-sparkling-2-line",
          items: [
            { slug: "residential-cleaning", label: isEn ? "Residential Cleaning" : "Limpieza Residencial" },
            { slug: "deep-cleaning", label: isEn ? "Deep Cleaning" : "Limpieza Profunda" },
            { slug: "move-in-cleaning", label: isEn ? "Move-in Cleaning" : "Limpieza de Entrada" },
            { slug: "move-out-cleaning", label: isEn ? "Move-out Cleaning" : "Limpieza de Salida" },
            { slug: "airbnb-cleaning", label: isEn ? "Airbnb Cleaning" : "Limpieza Airbnb" },
            { slug: "window-cleaning", label: isEn ? "Window Cleaning" : "Limpieza de Ventanas" },
          ],
        },
      ],
    },
    {
      category: isEn ? "Odd Jobs" : "Trabajos Varios",
      icon: "ri-hammer-line",
      color: "text-amber-600",
      bgColor: "bg-amber-100",
      subcategories: [
        {
          label: isEn ? "Quick Services" : "Servicios Rápidos",
          icon: "ri-hammer-line",
          items: [
            { slug: "house-cleaning-odd", label: isEn ? "House Cleaning" : "Limpieza del Hogar" },
            { slug: "airbnb-cleaning-odd", label: isEn ? "Airbnb Cleaning" : "Limpieza Airbnb" },
            { slug: "handyman-small-repairs", label: isEn ? "Small Repairs" : "Reparaciones Pequeñas" },
          ],
        },
      ],
    },
  ];

  const allServiceLinks = megaMenu.flatMap((cat) =>
    cat.subcategories.flatMap((sub) => sub.items)
  );

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled || menuOpen ? "bg-white shadow-sm" : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 md:px-6">
        <div className="flex items-center justify-between h-16 md:h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-3 cursor-pointer">
            <img
              src="https://storage.readdy-site.link/project_files/bbc9d585-3730-407a-ad80-463c6844ded8/016cccb4-6d9f-42db-b639-40ff0b2a2b7e_adry.png?v=d8b4f5cb5cb55fdf4705f60bba5131a8"
              alt="Adry & Mich Cleaning Logo"
              className="w-14 h-14 md:w-16 md:h-16 object-contain rounded-full"
            />
            <div className="hidden sm:block">
              <p className={`font-heading font-bold text-sm md:text-base leading-tight transition-colors ${scrolled || menuOpen ? "text-brand-dark" : "text-white"}`}>
                Adry &amp; Mich
              </p>
              <p className={`font-body text-xs transition-colors ${scrolled || menuOpen ? "text-brand-green" : "text-green-300"}`}>
                Cleaning &amp; Handyman
              </p>
            </div>
          </Link>

          {/* Desktop Nav */}
          <div className="hidden md:flex items-center gap-1">
            <Link
              to="/"
              className={`px-3 py-2 rounded-md font-body text-sm font-medium transition-colors cursor-pointer whitespace-nowrap ${
                location.pathname === "/"
                  ? "text-brand-green"
                  : scrolled ? "text-brand-dark hover:text-brand-green" : "text-white hover:text-green-300"
              }`}
            >
              {t("nav.home")}
            </Link>

            {/* Services Mega Dropdown */}
            <div
              className="relative"
              onMouseEnter={() => setServicesOpen(true)}
              onMouseLeave={() => setServicesOpen(false)}
            >
              <Link
                to="/services"
                className={`flex items-center gap-1 px-3 py-2 rounded-md font-body text-sm font-medium transition-colors cursor-pointer whitespace-nowrap ${
                  isActive("/services")
                    ? "text-brand-green"
                    : scrolled ? "text-brand-dark hover:text-brand-green" : "text-white hover:text-green-300"
                }`}
              >
                {t("nav.services")}
                <i className={`ri-arrow-down-s-line text-xs transition-transform ${servicesOpen ? "rotate-180" : ""}`}></i>
              </Link>

              {servicesOpen && (
                <div className="absolute top-full left-1/2 -translate-x-1/2 mt-1 w-[860px] bg-white rounded-2xl shadow-xl border border-gray-100 p-6">
                  <div className="grid grid-cols-3 gap-6">
                    {megaMenu.map((cat) => (
                      <div key={cat.category}>
                        {/* Category Header */}
                        <div className="flex items-center gap-2 mb-3 pb-2 border-b border-gray-100">
                          <div className={`w-7 h-7 flex items-center justify-center rounded-lg ${cat.bgColor}`}>
                            <i className={`${cat.icon} ${cat.color} text-sm`}></i>
                          </div>
                          <span className={`font-heading font-bold text-xs uppercase tracking-wider ${cat.color}`}>
                            {cat.category}
                          </span>
                        </div>

                        {/* Subcategories */}
                        {cat.subcategories.map((sub) => (
                          <div key={sub.label} className="mb-3">
                            <div className="flex items-center gap-1.5 mb-1.5 px-1">
                              <i className={`${sub.icon} text-gray-400 text-xs`}></i>
                              <span className="text-xs font-heading font-semibold text-gray-400 uppercase tracking-wide">
                                {sub.label}
                              </span>
                            </div>
                            {sub.items.map((item) => (
                              <Link
                                key={item.slug}
                                to={`/services/${item.slug}`}
                                className="flex items-center gap-2 px-2 py-1.5 rounded-lg text-sm text-gray-700 hover:bg-gray-50 hover:text-brand-green transition-colors cursor-pointer group"
                              >
                                <i className="ri-arrow-right-s-line text-gray-300 group-hover:text-brand-green text-xs"></i>
                                {item.label}
                              </Link>
                            ))}
                          </div>
                        ))}
                      </div>
                    ))}
                  </div>
                  <div className="mt-4 pt-4 border-t border-gray-100 flex justify-end">
                    <Link
                      to="/services"
                      className="inline-flex items-center gap-1.5 text-sm font-heading font-semibold text-brand-green hover:gap-2.5 transition-all cursor-pointer"
                    >
                      {isEn ? "View all services" : "Ver todos los servicios"}
                      <i className="ri-arrow-right-line"></i>
                    </Link>
                  </div>
                </div>
              )}
            </div>

            <Link
              to="/about"
              className={`px-3 py-2 rounded-md font-body text-sm font-medium transition-colors cursor-pointer whitespace-nowrap ${
                isActive("/about")
                  ? "text-brand-green"
                  : scrolled ? "text-brand-dark hover:text-brand-green" : "text-white hover:text-green-300"
              }`}
            >
              {t("nav.about")}
            </Link>
            <Link
              to="/testimonials"
              className={`px-3 py-2 rounded-md font-body text-sm font-medium transition-colors cursor-pointer whitespace-nowrap ${
                isActive("/testimonials")
                  ? "text-brand-green"
                  : scrolled ? "text-brand-dark hover:text-brand-green" : "text-white hover:text-green-300"
              }`}
            >
              {t("nav.testimonials")}
            </Link>
            <Link
              to="/contact"
              className={`px-3 py-2 rounded-md font-body text-sm font-medium transition-colors cursor-pointer whitespace-nowrap ${
                isActive("/contact")
                  ? "text-brand-green"
                  : scrolled ? "text-brand-dark hover:text-brand-green" : "text-white hover:text-green-300"
              }`}
            >
              {t("nav.contact")}
            </Link>
          </div>

          {/* Right side */}
          <div className="flex items-center gap-2 md:gap-3">
            <button
              onClick={toggleLang}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full border text-xs font-heading font-semibold transition-all cursor-pointer whitespace-nowrap ${
                scrolled || menuOpen
                  ? "border-gray-200 text-brand-dark hover:border-brand-green hover:text-brand-green"
                  : "border-white/40 text-white hover:border-white"
              }`}
            >
              <i className="ri-translate-2 text-sm"></i>
              {i18n.language === "en" ? "ES" : "EN"}
            </button>

            <a
              href="https://wa.me/15612626118"
              target="_blank"
              rel="noopener noreferrer"
              className="hidden md:flex items-center gap-2 bg-brand-green hover:bg-brand-green-dark text-white px-4 py-2 rounded-full text-sm font-heading font-semibold transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-whatsapp-line"></i>
              {t("nav.getQuote")}
            </a>

            <button
              onClick={() => setMenuOpen(!menuOpen)}
              className={`md:hidden w-9 h-9 flex items-center justify-center rounded-lg transition-colors cursor-pointer ${
                scrolled || menuOpen ? "text-brand-dark" : "text-white"
              }`}
            >
              <i className={`text-xl ${menuOpen ? "ri-close-line" : "ri-menu-line"}`}></i>
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Menu */}
      {menuOpen && (
        <div className="md:hidden bg-white border-t border-gray-100 px-4 py-4 max-h-[80vh] overflow-y-auto">
          <div className="flex flex-col gap-1">
            <Link to="/" className="px-3 py-2.5 rounded-lg text-sm font-body font-medium text-brand-dark hover:bg-gray-50 hover:text-brand-green transition-colors cursor-pointer">
              {t("nav.home")}
            </Link>

            {/* Mobile Services Toggle */}
            <button
              onClick={() => setMobileServicesOpen(!mobileServicesOpen)}
              className="flex items-center justify-between px-3 py-2.5 rounded-lg text-sm font-body font-medium text-brand-dark hover:bg-gray-50 hover:text-brand-green transition-colors cursor-pointer w-full text-left"
            >
              {t("nav.services")}
              <i className={`ri-arrow-down-s-line transition-transform ${mobileServicesOpen ? "rotate-180" : ""}`}></i>
            </button>

            {mobileServicesOpen && (
              <div className="pl-3 flex flex-col gap-3 mb-2">
                {megaMenu.map((cat) => (
                  <div key={cat.category}>
                    <div className="flex items-center gap-2 px-2 py-1">
                      <i className={`${cat.icon} ${cat.color} text-sm`}></i>
                      <span className={`text-xs font-heading font-bold uppercase tracking-wider ${cat.color}`}>
                        {cat.category}
                      </span>
                    </div>
                    {cat.subcategories.map((sub) => (
                      <div key={sub.label}>
                        {sub.items.map((item) => (
                          <Link
                            key={item.slug}
                            to={`/services/${item.slug}`}
                            className="flex items-center gap-2 px-4 py-1.5 rounded-lg text-xs text-gray-600 hover:text-brand-green hover:bg-gray-50 transition-colors cursor-pointer"
                          >
                            <i className="ri-arrow-right-s-line text-gray-300 text-xs"></i>
                            {item.label}
                          </Link>
                        ))}
                      </div>
                    ))}
                  </div>
                ))}
              </div>
            )}

            <Link to="/about" className="px-3 py-2.5 rounded-lg text-sm font-body font-medium text-brand-dark hover:bg-gray-50 hover:text-brand-green transition-colors cursor-pointer">
              {t("nav.about")}
            </Link>
            <Link to="/testimonials" className="px-3 py-2.5 rounded-lg text-sm font-body font-medium text-brand-dark hover:bg-gray-50 hover:text-brand-green transition-colors cursor-pointer">
              {t("nav.testimonials")}
            </Link>
            <Link to="/contact" className="px-3 py-2.5 rounded-lg text-sm font-body font-medium text-brand-dark hover:bg-gray-50 hover:text-brand-green transition-colors cursor-pointer">
              {t("nav.contact")}
            </Link>

            <a
              href="https://wa.me/15612626118"
              target="_blank"
              rel="noopener noreferrer"
              className="mt-2 flex items-center justify-center gap-2 bg-brand-green text-white px-4 py-3 rounded-full text-sm font-heading font-semibold cursor-pointer whitespace-nowrap"
            >
              <i className="ri-whatsapp-line"></i>
              {t("nav.getQuote")}
            </a>
          </div>
        </div>
      )}
    </nav>
  );
};

export default Navbar;
